import { getToken, removeToken } from './auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const WORKSPACE_STORAGE_KEY = 'eagle_workspace_id';

let workspaceIdForApi = (() => {
  try {
    return localStorage.getItem(WORKSPACE_STORAGE_KEY) || null;
  } catch {
    return null;
  }
})();

export const setWorkspaceIdForApi = (id) => {
  workspaceIdForApi = id;
};

export const clearWorkspaceIdForApi = () => {
  workspaceIdForApi = null;
};

export const getWorkspaceIdForApi = () => workspaceIdForApi;

export const publicApiRequest = async (endpoint, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (error) {
    console.error('Network error:', error);
    throw new Error('Network error: ' + error.message);
  }

  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (parseError) {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }

  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }

  return data;
};

export const validateAuthSession = async () => {
  const token = getToken();
  if (!token) {
    return { kind: 'invalid' };
  }
  let response;
  try {
    response = await fetch(`${API_URL}/auth/validate`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    console.error('Network error:', error);
    return { kind: 'transient' };
  }
  if (response.status === 401) {
    removeToken();
    return { kind: 'invalid' };
  }
  if (response.ok) {
    return { kind: 'ok' };
  }
  return { kind: 'transient' };
};

export const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (error) {
    console.error('Network error:', error);
    throw new Error('Network error: ' + error.message);
  }

  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (parseError) {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }

  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }

  return data;
};

export const login = async (email, password) => {
  // Must use publicApiRequest: apiRequest treats 401 as expired session and redirects to /login,
  // which breaks displaying "Invalid credentials" on failed login.
  return publicApiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
};

export const sendMagicLinkCode = async (email) => {
  return publicApiRequest('/auth/send-code', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

export const validateMagicLink = async (email, code) => {
  return publicApiRequest('/auth/validate', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
};

export const getAdminBackupOverview = async () => {
  return apiRequest('/admin/backup/overview');
};

export const downloadAdminWorkspaceBackup = async (workspaceId) => {
  const token = getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }
  const response = await fetch(
    `${API_URL}/admin/backup/workspaces/${encodeURIComponent(workspaceId)}/download`,
    { headers }
  );
  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const text = await response.text();
    let msg = text || 'Request failed';
    try {
      const j = JSON.parse(text);
      if (j.error) msg = j.error;
      else if (j.message) msg = j.message;
    } catch (_) {
      /* use msg */
    }
    throw new Error(msg);
  }
  const blob = await response.blob();
  const cd = response.headers.get('Content-Disposition');
  let filename = 'workspace-backup.zip';
  if (cd && cd.includes('filename=')) {
    const m = cd.match(/filename="([^"]+)"/) || cd.match(/filename=([^;\s]+)/);
    if (m) {
      filename = m[1].replace(/"/g, '');
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const adminBackupFormFetch = async (endpoint, formData) => {
  const token = getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData,
  });
  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }
  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }
  return data;
};

export const previewAdminBackupRestore = async (file) => {
  const form = new FormData();
  form.append('backup', file);
  return adminBackupFormFetch('/admin/backup/restore/preview', form);
};

export const restoreAdminBackup = async (file, workspaceName, userId) => {
  const form = new FormData();
  form.append('backup', file);
  form.append('workspace_name', workspaceName);
  form.append('user_id', userId);
  return adminBackupFormFetch('/admin/backup/restore', form);
};

export const getUsers = async () => {
  return apiRequest('/users');
};

export const getUser = async (id) => {
  return apiRequest(`/users/${id}`);
};

export const createUser = async (userData) => {
  return apiRequest('/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
};

export const updateUser = async (id, userData) => {
  return apiRequest(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  });
};

export const deleteUser = async (id) => {
  return apiRequest(`/users/${id}`, {
    method: 'DELETE',
  });
};

export const approveUser = async (id) => {
  return apiRequest(`/users/${id}/approve`, {
    method: 'POST',
  });
};

export const blockUser = async (id) => {
  return apiRequest(`/users/${id}/block`, {
    method: 'POST',
  });
};

export const getProducts = async () => {
  return apiRequest('/products');
};

export const getProduct = async (id) => {
  return apiRequest(`/products/${id}`);
};

export const createProduct = async (productData) => {
  return apiRequest('/products', {
    method: 'POST',
    body: JSON.stringify(productData),
  });
};

export const updateProduct = async (id, productData) => {
  return apiRequest(`/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(productData),
  });
};

export const deleteProduct = async (id) => {
  return apiRequest(`/products/${id}`, {
    method: 'DELETE',
  });
};

export const getSKUs = async (productId) => {
  const res = await apiRequest(`/products/${productId}/skus`);
  const list = res.data?.skus ?? (Array.isArray(res.data) ? res.data : []);
  return { ...res, data: list };
};

export const getSKU = async (skuId) => {
  return apiRequest(`/skus/${skuId}`);
};

export const createSKU = async (productId, skuData) => {
  return apiRequest(`/products/${productId}/skus`, {
    method: 'POST',
    body: JSON.stringify(skuData),
  });
};

export const updateSKU = async (skuId, skuData) => {
  return apiRequest(`/skus/${skuId}`, {
    method: 'PUT',
    body: JSON.stringify(skuData),
  });
};

export const deleteSKU = async (skuId) => {
  return apiRequest(`/skus/${skuId}`, {
    method: 'DELETE',
  });
};

export const getVariationCombinationSelections = async (productId) => {
  return apiRequest(`/products/${encodeURIComponent(productId)}/variation-combination-selections`);
};

export const setVariationCombinationSelections = async (productId, combinations) => {
  return apiRequest(`/products/${encodeURIComponent(productId)}/variation-combination-selections`, {
    method: 'PUT',
    body: JSON.stringify({ combinations }),
  });
};

export const listSupplyPurchases = async (productId, skuId) => {
  const res = await apiRequest(
    `/products/${encodeURIComponent(productId)}/skus/${encodeURIComponent(skuId)}/supply-purchases`,
  );
  return res?.data ?? res;
};

export const addSupplyPurchase = async (productId, skuId, payload) => {
  return apiRequest(
    `/products/${encodeURIComponent(productId)}/skus/${encodeURIComponent(skuId)}/supply-purchases`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
};

export const deleteSupplyPurchase = async (productId, skuId, purchaseId) => {
  return apiRequest(
    `/products/${encodeURIComponent(productId)}/skus/${encodeURIComponent(skuId)}/supply-purchases/${encodeURIComponent(purchaseId)}`,
    {
      method: 'DELETE',
    },
  );
};

// Product Attributes (Python API: data.attributes; legacy may use data as array)
export const getProductAttributes = async (productId) => {
  const res = await apiRequest(`/products/${productId}/attributes`);
  const inner = res.data;
  const list = Array.isArray(inner)
    ? inner
    : inner?.attributes ?? (Array.isArray(res.attributes) ? res.attributes : []);
  return { ...res, data: list };
};

export const createProductAttribute = async (productId, attributeData) => {
  return apiRequest(`/products/${productId}/attributes`, {
    method: 'POST',
    body: JSON.stringify(attributeData),
  });
};

export const updateProductAttribute = async (productId, attributeId, attributeData) => {
  return apiRequest(`/products/${productId}/attributes/${attributeId}`, {
    method: 'PUT',
    body: JSON.stringify(attributeData),
  });
};

export const getAffectedSKUsByAttribute = async (productId, attributeId) => {
  return apiRequest(`/products/${productId}/attributes/${attributeId}/affected-skus`);
};

export const deleteProductAttribute = async (productId, attributeId, confirm = false) => {
  const url = confirm ? `/products/${productId}/attributes/${attributeId}?confirm=true` : `/products/${productId}/attributes/${attributeId}`;
  return apiRequest(url, {
    method: 'DELETE',
  });
};

// Product Attribute Options
export const createProductAttributeOption = async (productId, attributeId, optionData) => {
  return apiRequest(`/products/${productId}/attributes/${attributeId}/options`, {
    method: 'POST',
    body: JSON.stringify(optionData),
  });
};

export const getAffectedSKUsByOption = async (productId, attributeId, optionId) => {
  return apiRequest(`/products/${productId}/attributes/${attributeId}/options/${optionId}/affected-skus`);
};

export const deleteProductAttributeOption = async (productId, attributeId, optionId, confirm = false) => {
  const url = confirm ? `/products/${productId}/attributes/${attributeId}/options/${optionId}?confirm=true` : `/products/${productId}/attributes/${attributeId}/options/${optionId}`;
  return apiRequest(url, {
    method: 'DELETE',
  });
};

export const getWorkspaces = async () => {
  return apiRequest('/workspaces');
};

export const createWorkspace = async (data) => {
  return apiRequest('/workspaces', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const deleteWorkspace = async (id) => {
  return apiRequest(`/workspaces/${id}`, {
    method: 'DELETE',
  });
};

export const getMyProfile = async () => {
  return apiRequest('/auth/me');
};

export const updateMyProfile = async (data) => {
  return apiRequest('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const uploadPhoto = async (file) => {
  const token = getToken();
  const formData = new FormData();
  formData.append('photo', file);

  const response = await fetch(`${API_URL}/auth/me/photo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Upload failed');
  }

  return data;
};

export const getMedias = async () => {
  return apiRequest('/medias');
};

export const getMedia = async (id) => {
  return apiRequest(`/medias/${id}`);
};

export const uploadMedia = async (formData) => {
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (workspaceIdForApi) headers['X-Workspace-ID'] = workspaceIdForApi;

  const response = await fetch(`${API_URL}/medias`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Upload failed');
  }

  return data;
};

export const deleteMedia = async (id) => {
  return apiRequest(`/medias/${id}`, {
    method: 'DELETE',
  });
};

export const getMediaLinks = async (id) => {
  return apiRequest(`/medias/${id}/links`);
};

export const linkMedia = async (mediaId, metadata) => {
  return apiRequest(`/medias/${mediaId}/link`, {
    method: 'POST',
    body: JSON.stringify({ media_id: mediaId, metadata }),
  });
};

export const unlinkMedia = async (mediaId, metadataKey, metadataValue) => {
  return apiRequest(`/medias/${mediaId}/unlink`, {
    method: 'POST',
    body: JSON.stringify({ metadata_key: metadataKey, metadata_value: metadataValue }),
  });
};

export const updateMediaOrder = async (metadataKey, entityId, mediaOrders) => {
  return apiRequest('/medias/order', {
    method: 'POST',
    body: JSON.stringify({
      metadata_key: metadataKey,
      entity_id: entityId,
      media_orders: mediaOrders,
    }),
  });
};

export const updateMediaMetadata = async (mediaId, metadataKey, entityId, title, url, isPublic) => {
  const body = {
    metadata_key: metadataKey,
    entity_id: entityId,
    title: title ?? '',
    url: url ?? '',
  };
  if (typeof isPublic === 'boolean') {
    body.public = isPublic;
  }
  return apiRequest(`/medias/${mediaId}/metadata`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

export const getCustomers = async () => {
  return apiRequest('/customers');
};

export const getCustomer = async (id) => {
  return apiRequest(`/customers/${id}`);
};

export const createCustomer = async (data) => {
  return apiRequest('/customers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateCustomer = async (id, data) => {
  return apiRequest(`/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteCustomer = async (id) => {
  return apiRequest(`/customers/${id}`, {
    method: 'DELETE',
  });
};

export const getOrders = async (query = {}) => {
  const q = new URLSearchParams();
  if (query.sort) q.set('sort', String(query.sort));
  if (query.order) q.set('order', String(query.order));
  if (typeof query.page === 'number') q.set('page', String(query.page));
  if (typeof query.page_size === 'number') q.set('page_size', String(query.page_size));
  const s = String(query.q || '').trim();
  if (s) q.set('q', s);
  if (query.status_filter === 'none') q.set('status_filter', 'none');
  const statuses = query.status;
  if (Array.isArray(statuses)) {
    for (const s of statuses) {
      const t = String(s).trim();
      if (t) q.append('status', t);
    }
  }
  const qs = q.toString();
  return apiRequest(`/orders${qs ? `?${qs}` : ''}`);
};

export const getOrder = async (id) => {
  return apiRequest(`/orders/${id}`);
};

/** Soft-delete (hide) an order from lists when it has no line items; backend clears client link and some medias. */
export const softDeleteOrder = async (id) => {
  return apiRequest(`/orders/${id}`, {
    method: 'DELETE',
  });
};

export const createOrder = async (data) => {
  return apiRequest('/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const createOrderFromSource = async (data) => {
  return apiRequest('/orders/create-from-order', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/** Merge data from another order into an existing order (adds lines, appends history, etc.). */
export const importOrderDataInto = async (orderId, data) => {
  return apiRequest(`/orders/${orderId}/import-from`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const listOrderStatusDefinitions = async () => {
  return apiRequest('/orders/workflow/status-definitions');
};

export const createOrderStatusDefinition = async (body) => {
  return apiRequest('/orders/workflow/status-definitions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const updateOrderStatusDefinition = async (id, body) => {
  return apiRequest(`/orders/workflow/status-definitions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

export const deleteOrderStatusDefinition = async (id) => {
  return apiRequest(`/orders/workflow/status-definitions/${id}`, {
    method: 'DELETE',
  });
};

/** When reassignToStatusId is set, moves orders/history to that status and removes the definition (and type links). */
export const removeOrderStatusDefinition = async (id, reassignToStatusId) => {
  const to = (reassignToStatusId || '').trim();
  if (to) {
    return apiRequest(`/orders/workflow/status-definitions/${id}/delete`, {
      method: 'POST',
      body: JSON.stringify({ reassign_to_status_id: to }),
    });
  }
  return deleteOrderStatusDefinition(id);
};

export const listOrderTypes = async (activeOnly = false) => {
  const q = activeOnly ? '?active_only=1' : '';
  return apiRequest(`/orders/workflow/order-types${q}`);
};

export const changeOrderType = async (orderId, body) => {
  return apiRequest(`/orders/${orderId}/change-order-type`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const createOrderType = async (body) => {
  return apiRequest('/orders/workflow/order-types', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const updateOrderType = async (id, body) => {
  return apiRequest(`/orders/workflow/order-types/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

export const deleteOrderType = async (id) => {
  return apiRequest(`/orders/workflow/order-types/${id}`, {
    method: 'DELETE',
  });
};

export const getOrderTypeStatuses = async (typeId) => {
  return apiRequest(`/orders/workflow/order-types/${typeId}/statuses`);
};

export const replaceOrderTypeStatuses = async (typeId, body) => {
  return apiRequest(`/orders/workflow/order-types/${typeId}/statuses`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

export const removeOrderTypeStatus = async (typeId, statusId, body) => {
  return apiRequest(`/orders/workflow/order-types/${typeId}/statuses/${statusId}/remove`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
};

export const updateOrder = async (id, data) => {
  return apiRequest(`/orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

const paymentEntryMultipartRequest = async (method, path, body, receiptFile) => {
  const fd = new FormData();
  fd.append('data', JSON.stringify(body));
  if (receiptFile) {
    fd.append('receipt', receiptFile);
  }
  const token = getToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }
  let response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: fd,
    });
  } catch (error) {
    console.error('Network error:', error);
    throw new Error('Network error: ' + error.message);
  }
  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (parseError) {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }
  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }
  return data;
};

export const createOrderPaymentEntry = async (orderId, body, receiptFile) => {
  if (receiptFile) {
    return paymentEntryMultipartRequest('POST', `/orders/${orderId}/payment-entries`, body, receiptFile);
  }
  return apiRequest(`/orders/${orderId}/payment-entries`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const updateOrderPaymentEntry = async (orderId, entryId, body, receiptFile) => {
  if (receiptFile) {
    return paymentEntryMultipartRequest('PATCH', `/orders/${orderId}/payment-entries/${entryId}`, body, receiptFile);
  }
  return apiRequest(`/orders/${orderId}/payment-entries/${entryId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

/** Opens receipt in a new tab; URL bar shows /orders/{orderId}/payments/{entryId}/receipt (SPA). */
export const openOrderPaymentEntryReceipt = (orderId, entryId) => {
  const path = `/orders/${orderId}/payments/${entryId}/receipt`;
  const url = `${window.location.origin}${path}`;
  const w = window.open(url, '_blank', 'noopener,noreferrer');
  if (!w) {
    return Promise.reject(new Error('Popup blocked'));
  }
  return Promise.resolve();
};

export const deleteOrderPaymentEntry = async (orderId, entryId) => {
  return apiRequest(`/orders/${orderId}/payment-entries/${entryId}`, {
    method: 'DELETE',
  });
};

export const patchOrderStatusHistory = async (orderId, historyId, body) => {
  return apiRequest(`/orders/${orderId}/status-history/${historyId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

export const deleteOrderStatusHistory = async (orderId, historyId) => {
  return apiRequest(`/orders/${orderId}/status-history/${historyId}`, {
    method: 'DELETE',
  });
};

export const approveOrder = async (id) => {
  return apiRequest(`/orders/${id}/approve`, {
    method: 'POST',
  });
};

export const addOrderLine = async (orderId, data) => {
  return apiRequest(`/orders/${orderId}/lines`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const addOrderLineItem = async (orderId, lineId, data) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateOrderLine = async (orderId, lineId, data) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteOrderLine = async (orderId, lineId) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}`, {
    method: 'DELETE',
  });
};

export const updateOrderLineItem = async (orderId, lineId, itemId, data) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteOrderLineItem = async (orderId, lineId, itemId) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}`, {
    method: 'DELETE',
  });
};

export const getOrderCustomizationFields = async (orderId) => {
  return apiRequest(`/orders/${orderId}/customization-fields`);
};

export const upsertOrderCustomizationFields = async (orderId, fields) => {
  return apiRequest(`/orders/${orderId}/customization-fields`, {
    method: 'PUT',
    body: JSON.stringify({ fields }),
  });
};

export const upsertOrderLineItemCustomizationFields = async (orderId, lineId, itemId, fields) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}/customization-fields`, {
    method: 'PUT',
    body: JSON.stringify({ fields }),
  });
};

export const deleteOrderLineItemCustomizationField = async (orderId, lineId, itemId, fieldId) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}/customization-fields/${fieldId}`, {
    method: 'DELETE',
  });
};

export const getOrderPublicForm = async (workspaceId, token) => {
  return publicApiRequest(`/public/orders/form/${workspaceId}/${token}`);
};

export const submitOrderPublicForm = async (workspaceId, token, data) => {
  return publicApiRequest(`/public/orders/form/${workspaceId}/${token}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const uploadOrderPublicFormMedia = async (workspaceId, token, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(
    `${API_URL}/public/orders/form/${encodeURIComponent(workspaceId)}/${encodeURIComponent(token)}/media`,
    {
      method: 'POST',
      body: formData,
    }
  );
  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    if (!response.ok) {
      throw new Error(text || 'Upload failed');
    }
    throw new Error('Invalid response from server');
  }
  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Upload failed');
  }
  return data;
};

export const getQuickIntakeOrderTypes = async (workspaceId) => {
  return publicApiRequest(`/public/orders/quick-intake/${encodeURIComponent(workspaceId)}/order-types`);
};

export const submitQuickIntakeOrder = async (workspaceId, formData) => {
  const response = await fetch(
    `${API_URL}/public/orders/quick-intake/${encodeURIComponent(workspaceId)}`,
    {
      method: 'POST',
      body: formData,
    }
  );
  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }
  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }
  return data;
};

export const getDashboardOverview = async () => {
  return apiRequest('/dashboard/overview');
};

export const getSettings = async () => {
  return apiRequest('/settings');
};

export const updateSettings = async (data) => {
  return apiRequest('/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const getExtensions = async () => {
  return apiRequest('/extensions');
};

export const enableExtension = async (id) => {
  return apiRequest(`/extensions/${encodeURIComponent(id)}/enable`, { method: 'POST' });
};

export const disableExtension = async (id) => {
  return apiRequest(`/extensions/${encodeURIComponent(id)}/disable`, { method: 'POST' });
};
